import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Receipt, Paperclip } from "lucide-react";
import { PageHeader, EmptyState, ConfirmDelete } from "@/components/Shared";
import { ExpenseDialog } from "@/components/ExpenseDialog";
import { DateFilters } from "@/components/DateFilters";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, fmtDate, fmtMoney, openReceipt, CATEGORIES, EXPENSE_GROUPS, groupOf } from "@/lib/api";
import { useSubscription } from "@/context/SubscriptionContext";

const ReceiptLink = ({ e }) =>
  e.receipt_id ? (
    <button onClick={() => openReceipt(e.receipt_id).catch((err) => toast.error(errMsg(err)))} className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 hover:text-emerald-900" data-testid={`expense-receipt-view-${e.id}`}>
      <Paperclip className="w-4 h-4" /> View
    </button>
  ) : (
    <span className="text-slate-400 text-sm">—</span>
  );

const Actions = ({ id, onEdit, onDelete }) => (
  <div className="flex items-center gap-1 justify-end">
    <button onClick={onEdit} className="h-10 w-10 rounded-lg flex items-center justify-center text-slate-600 hover:bg-slate-100" aria-label="Edit" data-testid={`expense-edit-${id}`}>
      <Pencil className="w-4 h-4" />
    </button>
    <button onClick={onDelete} className="h-10 w-10 rounded-lg flex items-center justify-center text-red-600 hover:bg-red-50" aria-label="Delete" data-testid={`expense-delete-${id}`}>
      <Trash2 className="w-4 h-4" />
    </button>
  </div>
);

const CatBadge = ({ c }) => {
  const g = groupOf(c);
  return (
    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ring-1 ${g.badge}`} title={g.label} data-testid={`expense-badge-${g.key}`}>
      {c}
    </span>
  );
};

const Legend = () => (
  <div className="flex flex-wrap gap-x-5 gap-y-2 mb-4 px-1 text-xs text-slate-600" data-testid="expense-classification-legend">
    {EXPENSE_GROUPS.map((g) => (
      <span key={g.key} className="flex items-center gap-2">
        <span className={`w-3 h-3 rounded-full ring-1 ${g.badge}`} />
        {g.short}
      </span>
    ))}
  </div>
);

const ExpenseTable = ({ items, onEdit, onDelete }) => (
  <div className="mt-card overflow-hidden hidden md:block" data-testid="expenses-table">
    <table className="w-full text-left">
      <thead className="bg-slate-50 border-b border-slate-200">
        <tr className="mt-overline">
          <th className="px-5 py-3.5">Date</th>
          <th className="px-5 py-3.5">Category</th>
          <th className="px-5 py-3.5">Description</th>
          <th className="px-5 py-3.5">Receipt</th>
          <th className="px-5 py-3.5 text-right">Amount</th>
          <th className="px-5 py-3.5 text-right">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {items.map((e) => (
          <tr key={e.id} className="hover:bg-slate-50/70 transition-colors" data-testid={`expense-row-${e.id}`}>
            <td className="px-5 py-4 font-medium text-slate-800 whitespace-nowrap">{fmtDate(e.date)}</td>
            <td className="px-5 py-4"><CatBadge c={e.category} /></td>
            <td className="px-5 py-4 text-slate-700 max-w-[320px] truncate">{e.description || "—"}</td>
            <td className="px-5 py-4"><ReceiptLink e={e} /></td>
            <td className="px-5 py-4 text-right font-mono font-bold text-slate-900">{fmtMoney(e.amount)}</td>
            <td className="px-5 py-2"><Actions id={e.id} onEdit={() => onEdit(e)} onDelete={() => onDelete(e)} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ExpenseCards = ({ items, onEdit, onDelete }) => (
  <div className="md:hidden space-y-3" data-testid="expenses-cards">
    {items.map((e) => (
      <div key={e.id} className="mt-card p-4" data-testid={`expense-card-${e.id}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm text-slate-500">{fmtDate(e.date)}</p>
            <p className="font-semibold text-slate-900 truncate mt-0.5">{e.description || e.category}</p>
          </div>
          <p className="font-mono text-xl font-bold text-slate-900 shrink-0">{fmtMoney(e.amount)}</p>
        </div>
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-3"><CatBadge c={e.category} /><ReceiptLink e={e} /></div>
          <Actions id={`m-${e.id}`} onEdit={() => onEdit(e)} onDelete={() => onDelete(e)} />
        </div>
      </div>
    ))}
  </div>
);

export default function Expenses() {
  const [range, setRange] = useState({ start: "", end: "" });
  const [category, setCategory] = useState("all");
  const [items, setItems] = useState(null);
  const { requirePaid } = useSubscription();
  const [dialog, setDialog] = useState({ open: false, expense: null });
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => {
    const params = { start: range.start || undefined, end: range.end || undefined, category: category === "all" ? undefined : category };
    return api.get("/expenses", { params }).then(({ data }) => setItems(data));
  }, [range, category]);
  useEffect(() => { load(); }, [load]);

  const doDelete = async () => {
    try {
      await api.delete(`/expenses/${toDelete.id}`);
      toast.success("Expense deleted");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setToDelete(null);
  };

  const total = (items || []).reduce((s, e) => s + e.amount, 0);
  const edit = (e) => setDialog({ open: true, expense: e });

  return (
    <div data-testid="expenses-page">
      <PageHeader overline="Records" title="Expenses">
        <button onClick={requirePaid(() => setDialog({ open: true, expense: null }))} className="mt-btn-dark" data-testid="expenses-add-button">
          <Plus className="w-5 h-5" /> Add Expense
        </button>
      </PageHeader>
      <div className="mt-card p-4 sm:p-5 mb-5 grid lg:grid-cols-[1fr_240px] gap-5 items-end">
        <DateFilters start={range.start} end={range.end} onChange={setRange} />
        <div>
          <p className="mt-overline mb-1.5">Category</p>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-11" data-testid="expense-filter-category"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all" data-testid="expense-filter-category-all">All categories</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c} data-testid={`expense-filter-category-${c.toLowerCase()}`}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      {items && items.length > 0 && (
        <p className="mb-4 px-1 text-slate-600" data-testid="expenses-total">
          <span className="font-mono font-bold text-slate-900 text-lg">{fmtMoney(total)}</span> across {items.length} expense{items.length === 1 ? "" : "s"}
        </p>
      )}
      {items === null ? (
        <div className="h-48 mt-card animate-pulse" />
      ) : items.length === 0 ? (
        <EmptyState icon={Receipt} title="No expenses found" text="Add your first expense, or change the filters above." testId="expenses-empty" />
      ) : (
        <>
          <Legend />
          <ExpenseTable items={items} onEdit={edit} onDelete={setToDelete} />
          <ExpenseCards items={items} onEdit={edit} onDelete={setToDelete} />
        </>
      )}
      <ExpenseDialog open={dialog.open} expense={dialog.expense} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} onSaved={load} />
      <ConfirmDelete open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} title="Delete this expense?" onConfirm={doDelete} />
    </div>
  );
}
